import React from 'react';

import moment from 'moment';
import { useMutation, useQuery } from 'react-query';

import { BN } from '@coral-xyz/anchor';
import { Button, CircularProgress } from '@mui/material';

import { MICRO_TOKEN } from '../../constants';
import { useDistributorData } from '../../hooks/use-distributor-data';
import { useProof } from '../../hooks/use-merkle-tree';
import { useSolana } from '../../providers/solana';
import { updateClaimStatusApi } from '../../utils/apis/claim';
import CountDown from '@/components/count-down-content/count-down';

const Claim = () => {
  const { data: distributorData, error, isLoading } = useDistributorData();
  const startDateTimestamp = Number(distributorData?.startTs ?? 0);
  const isBeforeAirdropPeriod = startDateTimestamp > Date.now() / 1000;
  const startDate = moment(startDateTimestamp * 1000).format('DD-MM-YYYY');
  const isAirdropEnded = (distributorData?.endTs ?? 0) < Date.now() / 1000;
  const { startTs, endTs } = distributorData || { startTs: 0, endTs: 0 };
  const currentTime = new Date();

  const {
    connection,
    provider,
    program,
    ctx,
    distributorClient,
    mintPublicKey,
    merkleTreeVersion,
    distributor,
    publicKey,
    connected,
  } = useSolana();

  if (
    !connection ||
    !provider ||
    !program ||
    !ctx ||
    !distributorClient ||
    !mintPublicKey ||
    !merkleTreeVersion ||
    !distributor ||
    !publicKey
  ) {
    return <div>Loading...</div>;
  }

  const {
    data: { proof, index, amount },
    isLoading: isProofLoading,
    refetch: refetchProof,
  } = useProof(publicKey);

  const { mutate: claim, isLoading: isClaiming } = useMutation(
    async () => {
      const claimAmount = new BN(amount);
      const proofBufferArray = proof.map((p: string) => Buffer.from(p, 'hex'));
      const tx = await distributorClient.newClaim(
        mintPublicKey,
        new BN(index),
        claimAmount,
        proofBufferArray,
        new BN(merkleTreeVersion),
      );
      const signature = await tx.buildAndExecute();
      const latestBlockHash = await connection.getLatestBlockhash();
      await connection.confirmTransaction({
        blockhash: latestBlockHash.blockhash,
        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
        signature: signature,
      });
      await updateClaimStatusApi(publicKey);
    },
    {
      onSuccess: () => {
        refetchClaimStatus();
      },
    },
  );

  const {
    data: isClaimed,
    isLoading: isClaimInfoLoading,
    refetch: refetchClaimStatus,
  } = useQuery(
    ['isClaimed', publicKey],
    async () => {
      if (!publicKey) {
        throw new Error('Public key is null');
      }
      const claimStatusPDA = distributorClient.pda.claim_status(
        publicKey,
        distributor.key,
      );
      const accountInfo = await connection.getAccountInfo(claimStatusPDA.key);
      if (!accountInfo) {
        return false;
      }
      const claimStatus =
        await distributorClient.ctx.program.account.claimStatus.fetch(
          claimStatusPDA.key,
        );
      return claimStatus.isClaimed;
    },
    {
      enabled: connected,
      staleTime: 1 * 60 * 1000,
    },
  );

  return (
    <div className={'font-ms_serif size-full min-h-[200px] flex flex-col'}>
      {isProofLoading ? (
        <div className={'flex-1 flex items-center justify-center'}>
          <CircularProgress className="text-[#F0E9CF]" />
        </div>
      ) : proof.length === 0 ? (
        <div
          className={
            'flex-1 flex items-center justify-center text-[#F0E9CF] text-[16px] leading-[120%] text-shadow-24 uppercase '
          }
        >
          Sorry, you are not eligible
        </div>
      ) : (
        <>
          <div
            className={
              'flex flex-row justify-between items-center text-[#f0e9cf7a] text-[12px] leading-[16px] md:text-[14px] md:leading-[18px] py-[4px] px-[8px]'
            }
          >
            {currentTime.getTime() < startTs * 1000 ? (
              <>
                <div>Claim available in:</div>
                <CountDown date={new Date(startTs * 1000)} />
              </>
            ) : currentTime.getTime() > endTs * 1000 ? (
              <div>
                Claim window has ended!
              </div>
            ) : (
              <>
                <div>Claim ends in:</div>
                <CountDown date={new Date(endTs * 1000)} />
              </>
            )}
          </div>
          <div className={'h-[1px] w-full bg-[#f0e9cf14] my-[16px]'} />
          <div
            className={
              'text-[#F0E9CF] text-shadow-24 text-[16px] leading-[20px] mx-auto text-center pt-[16px]'
            }
          >
            $MATTLE TO CLAIM
          </div>
          <div className="w-full relative inline-block text-[78px] leading-[120%] lg:text-[100px] lg:leading-[120%] mx-auto mt-[8px] mb-[20px]">
            <div className="absolute inset-0 text-shadow-6012 lg:text-shadow-918 text-nowrap text-center">
              {`${amount / MICRO_TOKEN}`}
            </div>

            {/* Layer 2: gradient */}
            <div className="relative text-transparent bg-clip-text bg-gradient-to-b from-[#F8F1FF] to-[#B982EF] text-nowrap text-center">
              {`${amount / MICRO_TOKEN}`}
            </div>
          </div>
          <div className={'h-[1px] w-full bg-[#f0e9cf14] my-[16px]'} />
          {distributorData && (isBeforeAirdropPeriod || isAirdropEnded || isClaimed) ? (
            <Button
              className="w-full claim-button-disabled text-center text-[#f0e9cf3d] text-[16px] leading-[22px] font-ms_serif capitalize p-0"
              disabled={true}
            >
              <div className={'my-[-3px]'}>{isClaimed ? "Claimed": "Claim"}</div>
            </Button>
          ) : (
            <Button
              className="w-full claim-button text-center text-[#100D08E5] text-[16px] leading-[22px] font-ms_serif capitalize p-0"
              onClick={() => claim()}
              disabled={isClaiming || isClaimInfoLoading}
            >
              {isClaiming || isClaimInfoLoading ? (
                <CircularProgress
                  size={22}
                  className="text-[#F0E9CF] my-[-3px]"
                />
              ) : (
                <div className={'my-[-3px]'}>Claim</div>
              )}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default Claim;
